import { useState } from 'react';
import {
  Text,
  Flex,
  Divider,
  Button,
  Collapse,
  useDisclosure,
  useToast,
  Box,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useBillingService } from '../../../services/billingService';
import { useBilling } from '../../../store/client/BillingContext';
import CustomToast from '../../../common/CustomToast';
import GradientBorderCard from '../../../common/GradientBorderCard';
import RequestedEntitlementsList from './RequestedEntitlementsList';

const CANCELLABLE_STATUSES = ['pending', 'reviewing', 'amendment_requested'];

const PendingCustomPlanBanner = ({ customPlanRequest }) => {
  const billingService = useBillingService();
  const { refresh } = useBilling();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { isOpen: isDetailsOpen, onToggle: onToggleDetails } = useDisclosure({
    defaultIsOpen: true,
  });

  const requestedEntitlements =
    customPlanRequest?.requestedEntitlements ??
    customPlanRequest?.requested_entitlements ??
    {};

  if (
    !customPlanRequest ||
    !CANCELLABLE_STATUSES.includes(customPlanRequest.status)
  ) {
    return null;
  }

  const showToast = (status, title, description) => {
    toast({
      render: (props) => (
        <CustomToast
          status={status}
          title={title}
          description={description}
          onClose={props.onClose}
          id={props.id}
        />
      ),
    });
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await billingService.cancelCustomPlanRequest(customPlanRequest.id);
      await refresh();
      showToast(
        'success',
        'Request cancelled',
        'Your custom plan request has been cancelled.'
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0] || 'Something went wrong';
      showToast('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GradientBorderCard bg="white">
      <Flex justifyContent="space-between" alignItems="center">
        <Text textStyle="bodySmall">Custom plan</Text>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        mt={4}
        gap={2}
        direction={{ base: 'column', md: 'row' }}
      >
        <Text textStyle="heading4">Custom plan request under review</Text>
        <Flex
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={onToggleDetails}
        >
          <Text textStyle="bodyMedium" fontSize="16px" color="orangePrimary">
            {isDetailsOpen ? 'Hide details' : 'See details'}
          </Text>
          <ChevronDownIcon
            color="orangePrimary"
            transform={isDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.2s ease"
          />
        </Flex>
      </Flex>
      <Text textStyle="bodyMedium" my={4}>
        Our team is preparing your plan based on the features and usage you
        requested and will notify you via email and in-app once it’s ready,
        usually within 1 business day.
      </Text>
      <Collapse in={isDetailsOpen}>
        <Divider
          mt={8}
          opacity={0.6}
          border="0.95px solid"
          borderColor="orangePrimary"
        />
        <Box mt={8} mb={8}>
          <RequestedEntitlementsList
            requestedEntitlements={requestedEntitlements}
            title="Requested entitlements:"
          />
        </Box>
        <Divider
          opacity={0.6}
          border="0.95px solid"
          borderColor="orangePrimary"
        />
        <Flex justifyContent="flex-end" mt={6}>
          <Button
            variant="secondary"
            size="md"
            onClick={handleCancel}
            isLoading={loading}
            loadingText="Cancelling..."
          >
            <Text>Cancel Request</Text>
          </Button>
        </Flex>
      </Collapse>
    </GradientBorderCard>
  );
};

export default PendingCustomPlanBanner;
